import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { SendRequestType } from '@/lib/utils/shared/httpTools'
import httpTools from '@/lib/utils/shared/httpTools'
import type {
  BasicOrderShippingType,
  OrderShippingType,
} from '@/store/types/order'
import type { CustomerObjectType } from '@/store/types/user'
import { isProductOptionSubscription } from '@/lib/utils/shared/IsProductOptionSubscription'
import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import type { QuotesCallType } from '../quotesCallType'
import type { OrderModelTransactionItemType } from './orderHelpers/buildOrder'

type CartItem = {
  id: string
  quantity: number
  currency: string
  po: string
}

type Props = {
  customerId: string
  customerType: string
  items: CartItem[]
  market: SupportedAlpha2CountryCode
  shippingMethod: string
  token: string | undefined
  shipToAddress: OrderShippingType
  customer: CustomerObjectType | null
  transactionItem: OrderModelTransactionItemType[]
}

type BasicOrderProps = {
  customerId: string
  customerType: string
  items: CartItem[]
  market: SupportedAlpha2CountryCode
  shippingMethod: string
  shipToAddress: BasicOrderShippingType
  customer: CustomerObjectType | null
  token: string | null
}

export const HydraCalculateOrder = ({
  customerId,
  items,
  shippingMethod,
  shipToAddress,
  token,
  customerType,
  market,
  customer,
  transactionItem,
}: Props) => {
  const subscriptionItems = items.filter((item) =>
    isProductOptionSubscription({ po: item.po }),
  )

  const order = {
    order: {
      customer: {
        id: {
          unicity: customerId,
        },
        href: customerId
          ? `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/customers?id.unicity=${customerId}`
          : `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/customers?type=${customerType}`,
      },
      lines: {
        items: items.map((item) => ({
          item: {
            href: `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/items?id.unicity=${item.id}`,
          },
          quantity: item.quantity,
        })),
      },
      shipToAddress,
      shippingMethod: {
        href: `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/shippingmethods?${shippingMethod}`,
      },
      shipToName: {
        firstName: customer?.humanName.firstName,
        lastName: customer?.humanName.lastName,
      },
      type: subscriptionItems.length > 0 ? 'Autoship' : 'Order',
      source: {
        medium: 'Internet',
        agent: 'Shop2.0',
      },
      market: getAlpha2WithExceptions({ alpha2: market }),
      transactions: { items: transactionItem },
    },
  }
  const request: SendRequestType = {
    method: 'POST',
    url: `/quotes?proForma=true`,
    data: order,
    withAuth: true,
    token,
    withErrorHandling: false,
  }

  return httpTools.sendRequest<QuotesCallType>(request)
}

export const HydraCalculateBasicOrder = ({
  customerId,
  items,
  shippingMethod,
  shipToAddress,
  customerType,
  market,
  token,
}: BasicOrderProps) => {
  const subscriptionItems = items.filter((item) =>
    isProductOptionSubscription({ po: item.po }),
  )

  const order = {
    order: {
      customer: {
        href: customerId
          ? `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/customers?id.unicity=${customerId}`
          : `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/customers?type=${customerType}`,
      },
      lines: {
        items: items.map((item) => ({
          item: {
            href: `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/items?id.unicity=${item.id}`,
          },
          quantity: item.quantity,
        })),
      },
      shipToAddress,
      shippingMethod: {
        href: `${process.env.NEXT_PUBLIC_HYDRA_BASE_URL}/shippingmethods?${shippingMethod}`,
      },
      type: subscriptionItems.length > 0 ? 'Autoship' : 'Order',
      source: {
        medium: 'Internet',
        agent: 'Shop2.0',
      },
      market: getAlpha2WithExceptions({ alpha2: market }),
      transactions: { items: [] },
    },
  }

  let request: SendRequestType = {
    url: `/quotes`,
  }
  // if the user is logged in we can use th proForma. Let me know if this is okay for optimization reasons.
  // the quotes call has been extremely slow.
  if (token) {
    request = {
      method: 'POST',
      url: `/quotes?proForma=true`,
      data: order,
      withAuth: true,
      token,
      withErrorHandling: false,
    }
  } else {
    request = {
      method: 'POST',
      url: `/quotes`,
      data: order,
      withAuth: false,
      withErrorHandling: false,
    }
  }

  return httpTools.sendRequest<QuotesCallType>(request)
}
